/**
 * Fly-to-Cart Animation Module
 * Premium product image fly animation from add-to-cart origin to cart icon.
 *
 * Usage:
 *   FlyToCart.animate({
 *     triggerElement: <HTMLElement>,       // The button or element that was clicked
 *     imageUrl: <string>,                 // Product image URL (fallback if auto-detect fails)
 *     onComplete: <Function>              // Called after animation finishes
 *   });
 */

(function () {
  'use strict';

  const ANIMATION_DURATION = 780; // ms total flight
  const CLONE_SIZE = 80; // px - size of the flying image
  const TRAIL_COUNT = 3;
  const SPARKLE_COUNT = 8;

  /**
   * Find the nearest product image to the trigger element.
   * Handles PDP (images in sibling grid column), product cards, and quick-add modals.
   */
  function findProductImage(triggerElement) {
    if (!triggerElement) return null;

    // ---- Strategy 1: Walk up to a known product container ----
    const container = triggerElement.closest(
      'enhanced-product-card, product-info, .product, .card-enhanced, .product-card, .card-wrapper, quick-add-modal, form[action*="/cart/add"]'
    );

    if (container) {
      // PDP: look for the active/visible slide's image first
      const activeSlideImg =
        container.querySelector('.product__media-item.is-active .product__media img') ||
        container.querySelector('.product__media-item.is-active img') ||
        container.querySelector('.slider__slide.is-active .product__media img') ||
        container.querySelector('.slider__slide.is-active img');
      if (activeSlideImg && activeSlideImg.src) return activeSlideImg;

      // Product cards and general containers
      const selectors = [
        '.card-enhanced__image img',
        '.product__media img',
        '.product-media-container img',
        '.product__media-wrapper img',
        '.media img',
        '.card__media img',
        '.card__inner img',
        'img[srcset]',
        'img',
      ];

      for (const sel of selectors) {
        const img = container.querySelector(sel);
        if (img && img.src) return img;
      }
    }

    // ---- Strategy 2: Look in the closest section ----
    const section = triggerElement.closest('.shopify-section, section');
    if (section) {
      const sectionImg =
        section.querySelector('.product__media-item.is-active img') ||
        section.querySelector('.product__media img') ||
        section.querySelector('.product-media-container img') ||
        section.querySelector('.product__media-wrapper img') ||
        section.querySelector('.media img');
      if (sectionImg && sectionImg.src) return sectionImg;
    }

    // ---- Strategy 3: Global PDP fallback ----
    const globalImg =
      document.querySelector('.product__media-item.is-active .product__media img') ||
      document.querySelector('.product__media-item.is-active img') ||
      document.querySelector('product-info .product__media img') ||
      document.querySelector('product-info img[srcset]') ||
      document.querySelector('.product__media-wrapper img');
    if (globalImg && globalImg.src) return globalImg;

    return null;
  }

  /**
   * Get the cart icon element.
   */
  function getCartTarget() {
    return document.querySelector('#cart-icon-bubble');
  }

  /**
   * Attempt to find the tag-g element inside the cart icon for the swing.
   */
  function getTagSwingElement() {
    const cartIcon = getCartTarget();
    if (!cartIcon) return null;
    return cartIcon.querySelector('.tag-g') || cartIcon;
  }

  /**
   * Create the flying image clone element.
   */
  function createFlyingClone(imgSrc, sourceRect, size) {
    const wrapper = document.createElement('div');
    wrapper.className = 'fly-to-cart';

    const cloneSize = size || CLONE_SIZE;
    const aspectRatio = sourceRect ? sourceRect.width / sourceRect.height : 1;
    const width = cloneSize;
    const height = cloneSize / aspectRatio;

    wrapper.style.width = width + 'px';
    wrapper.style.height = height + 'px';

    const img = document.createElement('img');
    img.src = imgSrc;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(img);

    document.body.appendChild(wrapper);
    return { el: wrapper, width, height };
  }

  /**
   * Create sparkle particles at a point.
   */
  function createSparkles(x, y) {
    for (let i = 0; i < SPARKLE_COUNT; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'fly-to-cart-sparkle';

      const angle = (i / SPARKLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const distance = 16 + Math.random() * 18;
      const sx = Math.cos(angle) * distance;
      const sy = Math.sin(angle) * distance;

      sparkle.style.left = x + 'px';
      sparkle.style.top = y + 'px';
      sparkle.style.setProperty('--sx', sx + 'px');
      sparkle.style.setProperty('--sy', sy + 'px');

      sparkle.style.animation = `sparkleOut ${280 + Math.random() * 180}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`;

      document.body.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 550);
    }
  }

  /**
   * Create ripple effect at a point.
   */
  function createRipple(x, y) {
    const ripple = document.createElement('div');
    ripple.className = 'fly-to-cart-ripple';

    const size = 28;
    ripple.style.width = size + 'px';
    ripple.style.height = size + 'px';
    ripple.style.left = x - size / 2 + 'px';
    ripple.style.top = y - size / 2 + 'px';

    document.body.appendChild(ripple);
    setTimeout(() => ripple.remove(), 650);
  }

  /**
   * Subtle swing + gentle scale nudge on the cart icon's tag.
   * A refined, premium-feeling acknowledgment that something arrived.
   */
  function impactCartIcon() {
    const tagEl = getTagSwingElement();
    if (!tagEl) return;

    // Pause the idle sway, apply the subtle impact swing
    tagEl.style.animation = 'none';
    void tagEl.offsetWidth;
    tagEl.style.animation = 'cartTagImpactSwing 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards';

    // Gentle scale nudge on the cart icon wrapper
    const cartIcon = getCartTarget();
    if (cartIcon) {
      cartIcon.classList.remove('cart-icon-bump');
      void cartIcon.offsetWidth;
      cartIcon.classList.add('cart-icon-bump');
      cartIcon.addEventListener('animationend', () => {
        cartIcon.classList.remove('cart-icon-bump');
      }, { once: true });
    }

    // Resume gentle sway after impact settles
    setTimeout(() => {
      tagEl.style.animation = '';
    }, 750);
  }

  // =============================
  // Smooth easing functions
  // =============================

  /** Quadratic ease-out: fast start, gentle slow down */
  function easeOutQuad(t) {
    return 1 - (1 - t) * (1 - t);
  }

  /** Custom ease-in-out with more time spent in the "cruise" */
  function easeInOutCustom(t) {
    if (t < 0.15) {
      // Slow ease in (lift)
      const s = t / 0.15;
      return 0.08 * s * s;
    } else if (t < 0.85) {
      // Smooth cruise (arc)
      const s = (t - 0.15) / 0.7;
      return 0.08 + 0.84 * s;
    } else {
      // Ease out (landing)
      const s = (t - 0.85) / 0.15;
      return 0.92 + 0.08 * (1 - (1 - s) * (1 - s));
    }
  }

  /**
   * Compute a point along a quadratic Bézier curve.
   */
  function bezierPoint(t, p0, p1, p2) {
    const u = 1 - t;
    return {
      x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
    };
  }

  /**
   * Smooth interpolation for scale.
   */
  function getScale(t) {
    if (t < 0.15) {
      // Lift: 1 → 1.1
      const s = t / 0.15;
      return 1 + 0.1 * easeOutQuad(s);
    } else if (t < 0.8) {
      // Shrink: 1.1 → 0.3
      const s = (t - 0.15) / 0.65;
      return 1.1 - 0.8 * s;
    } else {
      // Land: 0.3 → 0
      const s = (t - 0.8) / 0.2;
      return 0.3 * (1 - easeOutQuad(s));
    }
  }

  /**
   * Main animation function using requestAnimationFrame for butter-smooth motion.
   */
  function animate(options = {}) {
    const { triggerElement, imageUrl, onComplete } = options;

    const cartIconEl = getCartTarget();
    if (!cartIconEl) {
      if (onComplete) onComplete();
      return;
    }

    // Find product image
    const productImg = findProductImage(triggerElement);
    const imgSrc = productImg?.src || imageUrl;

    if (!imgSrc) {
      if (onComplete) onComplete();
      return;
    }

    // Source position — prefer the product image, but if it's scrolled
    // off-screen (common on mobile PDP), fall back to the trigger button
    let sourceRect;
    if (productImg) {
      const imgRect = productImg.getBoundingClientRect();
      const viewH = window.innerHeight;
      const viewW = window.innerWidth;
      const isVisible = imgRect.bottom > 0 && imgRect.top < viewH && imgRect.right > 0 && imgRect.left < viewW;
      sourceRect = isVisible ? imgRect : (triggerElement ? triggerElement.getBoundingClientRect() : imgRect);
    } else if (triggerElement) {
      sourceRect = triggerElement.getBoundingClientRect();
    } else {
      if (onComplete) onComplete();
      return;
    }

    // Target position (cart icon center)
    const cartRect = cartIconEl.getBoundingClientRect();
    const targetX = cartRect.left + cartRect.width / 2;
    const targetY = cartRect.top + cartRect.height / 2;

    // Source center
    const sourceX = sourceRect.left + sourceRect.width / 2;
    const sourceY = sourceRect.top + sourceRect.height / 2;

    // Create flying clone — smaller on mobile for proportion
    const isMobile = window.innerWidth < 750;
    const cloneSize = isMobile ? 56 : CLONE_SIZE;
    const { el: clone, width: cloneW, height: cloneH } = createFlyingClone(imgSrc, sourceRect, cloneSize);

    // Position at source center
    const startLeft = sourceX - cloneW / 2;
    const startTop = sourceY - cloneH / 2;
    clone.style.left = startLeft + 'px';
    clone.style.top = startTop + 'px';

    // Bézier control point — arc curves upward
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    const cpX = sourceX + dx * 0.35;
    const cpY = Math.min(sourceY, targetY) - 100 - Math.abs(dx) * 0.12;

    const p0 = { x: sourceX, y: sourceY };
    const p1 = { x: cpX, y: cpY };
    const p2 = { x: targetX, y: targetY };

    // ---- Trail elements (pre-create, position via rAF) ----
    const trailEls = [];
    const trailHistory = []; // stores recent {x, y, scale, opacity} snapshots
    for (let i = 0; i < TRAIL_COUNT; i++) {
      const trail = document.createElement('div');
      trail.className = 'fly-to-cart-trail';
      trail.style.width = cloneW + 'px';
      trail.style.height = cloneH + 'px';
      trail.style.left = startLeft + 'px';
      trail.style.top = startTop + 'px';
      trail.style.opacity = '0';

      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = '';
      trail.appendChild(img);

      document.body.appendChild(trail);
      trailEls.push(trail);
    }

    // ---- Animation loop via requestAnimationFrame ----
    let startTime = null;
    let finished = false;
    const HISTORY_INTERVAL = 3; // store trail snapshot every N frames
    let frameCount = 0;

    function tick(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const t = Math.min(elapsed / ANIMATION_DURATION, 1);

      // Smooth curve position
      const curveT = easeInOutCustom(t);
      const pt = bezierPoint(curveT, p0, p1, p2);

      // Scale
      const scale = getScale(t);

      // Rotation: gentle tilt that follows the curve direction
      const rotation = Math.sin(t * Math.PI * 1.2) * 6;

      // Opacity: fade out at the very end
      const opacity = t > 0.88 ? Math.max(0, 1 - (t - 0.88) / 0.12) : 1;

      // Apply transform (translate relative to initial position)
      const tx = pt.x - sourceX;
      const ty = pt.y - sourceY;

      clone.style.transform = `translate(${tx}px, ${ty}px) scale(${Math.max(scale, 0)}) rotate(${rotation}deg)`;
      clone.style.opacity = opacity;

      // ---- Update trail history ----
      frameCount++;
      if (frameCount % HISTORY_INTERVAL === 0 && t < 0.85) {
        trailHistory.push({ tx, ty, scale, opacity });
        // Keep limited history
        if (trailHistory.length > TRAIL_COUNT + 2) trailHistory.shift();
      }

      // Position trails from history (staggered behind)
      for (let i = 0; i < TRAIL_COUNT; i++) {
        const histIdx = trailHistory.length - 1 - (i + 1) * 1;
        if (histIdx >= 0 && histIdx < trailHistory.length) {
          const h = trailHistory[histIdx];
          trailEls[i].style.transform = `translate(${h.tx}px, ${h.ty}px) scale(${Math.max(h.scale * 0.9, 0)})`;
          trailEls[i].style.opacity = (0.18 - i * 0.05) * (t < 0.85 ? 1 : 0);
        }
      }

      if (t < 1) {
        requestAnimationFrame(tick);
      } else if (!finished) {
        finished = true;
        onLand();
      }
    }

    function onLand() {
      // Landing effects
      createRipple(targetX, targetY);
      createSparkles(targetX, targetY);
      impactCartIcon();

      // Clean up clone + trails
      setTimeout(() => {
        clone.remove();
        trailEls.forEach((t) => t.remove());
      }, 60);

      // Fire callback after bump is visible
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 200);
    }

    // Start the loop
    requestAnimationFrame(tick);

    // Safety cleanup
    setTimeout(() => {
      if (!finished) {
        finished = true;
        clone.remove();
        trailEls.forEach((t) => t.remove());
        if (onComplete) onComplete();
      }
    }, ANIMATION_DURATION + 1200);
  }

  // Expose globally
  window.FlyToCart = { animate };
})();
